import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ChevronDown, ChevronUp, FileText, CreditCard, Database, Shield, Landmark, Brain, Building, Mail, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function FAQ() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [openSection, setOpenSection] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqSections = [
    {
      id: "invoicing",
      title: "Invoicing",
      icon: FileText,
      questions: [
        {
          q: "How do I create and send an invoice?",
          a: "Navigate to the Invoicing page from your dashboard. Click 'Create Invoice', fill in your client's details, add line items, and click 'Send'. The invoice will be emailed to your client with a secure payment link."
        },
        {
          q: "What payment methods do my clients have?",
          a: "Your clients can pay via credit card, debit card, or ACH bank transfer (for US clients). All payments are processed securely through Stripe."
        },
        {
          q: "Can I customize my invoice template?",
          a: "Yes! You can add your company logo, customize colors, add terms and conditions, and include notes on each invoice. Go to Settings > Company Profile to set up your branding."
        },
        {
          q: "How do I track invoice status?",
          a: "The Invoicing page shows all your invoices with their status: Draft, Sent, Viewed, Paid, or Overdue. You'll receive notifications when clients view or pay invoices."
        },
        {
          q: "Can I set up recurring invoices?",
          a: "Yes, when creating an invoice, enable 'Recurring' and set the frequency (weekly, monthly, quarterly, or annually). The system will automatically generate and send invoices on schedule."
        },
        {
          q: "What happens if a payment fails?",
          a: "If a payment fails, both you and your client will be notified. The client can retry the payment using the same link. Failed payments are logged for your records."
        }
      ]
    },
    {
      id: "payments",
      title: "Payment Processing",
      icon: CreditCard,
      questions: [
        {
          q: "How is payment processing handled?",
          a: "All payments are processed through Stripe, a PCI DSS Level 1 certified payment processor. We never store raw credit card numbers on our systems."
        },
        {
          q: "What are the payment processing fees?",
          a: "Standard Stripe fees apply (2.9% + $0.30 for card payments). Additionally, FinAI charges a platform fee based on your plan: 1% for standard users, 0% for Power users with their own Stripe integration."
        },
        {
          q: "How long until I receive my funds?",
          a: "Standard payouts arrive within 2-3 business days. Enterprise and Power users with auto-payout enabled receive funds faster. You can view payout status on the My Payouts page."
        },
        {
          q: "Can I use my own Stripe account?",
          a: "Yes! Power users can connect their own Stripe account to bypass platform fees entirely. Go to Settings > Stripe Integration to add your API keys."
        },
        {
          q: "Are payment links secure?",
          a: "Absolutely. Payment links use HTTPS encryption, are processed through PCI-compliant Stripe, and include fraud protection. Links can be set to expire and are trackable."
        },
        {
          q: "What currencies are supported?",
          a: "We currently support USD for all transactions. Multi-currency support is on our roadmap for future releases."
        },
        {
          q: "How do refunds work?",
          a: "You can issue full or partial refunds from the invoice detail page. Refunds are processed through Stripe and typically appear in 5-10 business days on your client's statement."
        }
      ]
    },
    {
      id: "data",
      title: "Data Processing & Security",
      icon: Database,
      questions: [
        {
          q: "How is my data protected?",
          a: "All data is encrypted at rest (AES-256) and in transit (TLS 1.3). We use enterprise-grade cloud infrastructure with SOC 2 certified data centers and implement strict access controls."
        },
        {
          q: "Where is my data stored?",
          a: "All data is stored in secure data centers within the United States. We do not transfer data outside the US without explicit consent."
        },
        {
          q: "Who has access to my financial data?",
          a: "Only you and any bookkeepers you've been assigned have access to your financial data. Our support team can access limited data only when necessary for troubleshooting, with full audit logging."
        },
        {
          q: "How long is my data retained?",
          a: "Financial records are retained for 7 years per IRS requirements. Account data is kept during active use plus 90 days. You can request data deletion at any time for non-required records."
        },
        {
          q: "Can I export my data?",
          a: "Yes! You can export all your data (invoices, transactions, documents) in CSV or PDF format from the Settings page. We support data portability."
        },
        {
          q: "Is FinAI GDPR/CCPA compliant?",
          a: "Yes. We comply with CCPA for California residents and follow GDPR-aligned practices. You can exercise your data rights by contacting compliance@revurge.com."
        },
        {
          q: "What happens if there's a data breach?",
          a: "We have a documented incident response plan. In the event of a breach, affected users will be notified within 72 hours with details and remediation steps."
        }
      ]
    },
    {
      id: "bank",
      title: "Bank Connections",
      icon: Landmark,
      questions: [
        {
          q: "How do bank connections work?",
          a: "We use Plaid, a trusted financial data platform, to securely connect to your bank. You authenticate directly with your bank using OAuth - we never see or store your bank login credentials."
        },
        {
          q: "Is connecting my bank account safe?",
          a: "Yes. Plaid uses bank-level 256-bit encryption and is SOC 2 Type II certified. Your credentials are transmitted directly to your bank through encrypted channels."
        },
        {
          q: "What information is accessed from my bank?",
          a: "With your permission, we access: account balances, transaction history, and account details. We do NOT have the ability to move money or make payments from your connected accounts."
        },
        {
          q: "Can I disconnect my bank account?",
          a: "Yes, you can disconnect any bank connection at any time from Settings > Bank Connections. Historical transaction data may be retained for record-keeping."
        },
        {
          q: "Why do I need to reconnect sometimes?",
          a: "Banks periodically require re-authentication for security. If your connection expires, you'll be prompted to reconnect. This is a security feature, not a bug."
        },
        {
          q: "Which banks are supported?",
          a: "We support over 11,000 financial institutions in the US through Plaid, including major banks, credit unions, and investment accounts."
        }
      ]
    },
    {
      id: "ai",
      title: "AI & Automation",
      icon: Brain,
      questions: [
        {
          q: "How is AI used in FinAI?",
          a: "AI powers document analysis, transaction categorization, expense classification, and financial insights. It helps automate manual bookkeeping tasks and identify patterns."
        },
        {
          q: "Is my data used to train AI models?",
          a: "No. Your data is not used to train general-purpose AI models. AI processing is specific to your account and data remains confidential."
        },
        {
          q: "How accurate is AI categorization?",
          a: "Our AI achieves over 95% accuracy for common transaction types. You can always review and correct AI suggestions, which helps improve accuracy for your specific business."
        },
        {
          q: "Can I disable AI features?",
          a: "Most AI features can be configured in Settings. However, some basic automation (like transaction syncing) is core to the platform's functionality."
        },
        {
          q: "Does AI replace my accountant?",
          a: "No. AI assists with bookkeeping automation but does not replace professional accounting advice. We recommend working with a qualified accountant for tax planning and complex financial decisions."
        }
      ]
    },
    {
      id: "security",
      title: "Account Security",
      icon: Shield,
      questions: [
        {
          q: "How do I enable two-factor authentication?",
          a: "Go to Settings > Security and enable 2FA. You can use an authenticator app (recommended) or SMS verification. We strongly recommend enabling 2FA for all accounts."
        },
        {
          q: "What should I do if my account is compromised?",
          a: "Immediately: 1) Change your password, 2) Enable 2FA if not already, 3) Review recent activity, 4) Contact security@revurge.com. We'll help secure your account and investigate."
        },
        {
          q: "How often should I change my password?",
          a: "We recommend changing your password every 90 days or immediately if you suspect compromise. Use a unique, strong password not used elsewhere."
        },
        {
          q: "Can team members have different access levels?",
          a: "Yes. Account owners can invite team members with specific roles: Admin (full access), Bookkeeper (assigned accounts only), or View-only access."
        },
        {
          q: "What compliance certifications do you have?",
          a: "We align with SOC 2 Type II, ISO 27001, PCI DSS, NIST CSF, and GLBA Safeguards Rule. See our Security page for full details."
        }
      ]
    }
  ];

  const filteredSections = searchQuery
    ? faqSections.map(section => ({
        ...section,
        questions: section.questions.filter(
          faq =>
            faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.questions.length > 0)
    : faqSections;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#121212] text-white py-6 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <span className="text-[#121212] font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold font-['Manrope']">FinAI</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms</Link>
            <Link to="/login" className="bg-[#BFFF00] text-[#121212] px-4 py-2 rounded-full font-medium hover:bg-[#a8e600]">Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F7FFE5] to-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#121212] font-['Manrope'] mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 text-lg mb-8">
            Find answers about invoicing, payments, data processing, and more
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-3 rounded-full border-gray-200 bg-white shadow-sm"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No FAQs found matching "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery("")}
              className="text-blue-600 hover:underline mt-2"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredSections.map((section) => (
              <div key={section.id} className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                  className="w-full flex items-center justify-between p-6 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
                      <section.icon className="w-5 h-5 text-[#121212]" />
                    </div>
                    <div className="text-left">
                      <h2 className="font-bold text-[#121212] text-lg">{section.title}</h2>
                      <p className="text-sm text-gray-500">{section.questions.length} questions</p>
                    </div>
                  </div>
                  {openSection === section.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {openSection === section.id && (
                  <div className="divide-y divide-gray-100">
                    {section.questions.map((faq, idx) => (
                      <div key={idx} className="bg-white">
                        <button
                          onClick={() => setOpenQuestion(openQuestion === `${section.id}-${idx}` ? null : `${section.id}-${idx}`)}
                          className="w-full flex items-start justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                        >
                          <span className="font-medium text-[#121212] pr-4">{faq.q}</span>
                          {openQuestion === `${section.id}-${idx}` ? (
                            <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                          )}
                        </button>
                        {openQuestion === `${section.id}-${idx}` && (
                          <div className="px-4 pb-4">
                            <p className="text-gray-600 text-sm leading-relaxed bg-[#F7FFE5] p-4 rounded-xl">
                              {faq.a}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 bg-[#121212] rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
          <p className="text-gray-400 mb-6">Our support team is here to help</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:support@revurge.com"
              className="inline-flex items-center gap-2 bg-[#BFFF00] text-[#121212] px-6 py-3 rounded-full font-medium hover:bg-[#a8e600] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </a>
            <a
              href="mailto:compliance@revurge.com"
              className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 rounded-full font-medium hover:bg-white/20 transition-colors"
            >
              <Shield className="w-4 h-4" />
              Compliance Inquiries
            </a>
          </div>
        </div>

        {/* Company Info */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Building className="w-4 h-4" />
            <span className="text-sm">Revurge Corporation | 8 The Green, Dover, DE 19901</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121212] text-white py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms of Service</Link>
            <Link to="/data-use" className="hover:text-[#BFFF00]">Data Use Policy</Link>
            <Link to="/cookies" className="hover:text-[#BFFF00]">Cookie Policy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/faq" className="hover:text-[#BFFF00]">FAQ</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} Revurge Corporation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
